import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import styles from './PdfViewer.module.css';

interface PdfViewerProps {
  content?: string | null;
  sourceUrl?: string | null;
  height?: number | string;
  className?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ content, sourceUrl, height = 480, className }) => {
  const fileUrl = sourceUrl?.trim() ? sourceUrl : undefined;

  const fileData = useMemo(() => {
    if (fileUrl || !content) {
      return undefined;
    }

    const trimmed = content.trim();
    if (!trimmed) {
      return undefined;
    }

    const normalized = trimmed.replace(/\s+/g, '');
    const dataUrlMatch = normalized.match(/^data:application\/pdf;base64,(.+)$/i);
    const base64Payload = dataUrlMatch ? dataUrlMatch[1] : normalized;
    const pdfHeaderIndex = content.indexOf('%PDF-');
    const hasEmbeddedPdfHeader = pdfHeaderIndex >= 0;
    const looksLikeBase64Pdf = normalized.startsWith('JVBERi0');
    const looksLikeBinaryPdf = trimmed.startsWith('%PDF-') || hasEmbeddedPdfHeader;
    const looksLikeDataUrlPdf = !!dataUrlMatch;

    if (!looksLikeBase64Pdf && !looksLikeBinaryPdf && !looksLikeDataUrlPdf) {
      return undefined;
    }

    let binaryPayload: string | null = null;

    if (looksLikeBinaryPdf) {
      const fromHeader = hasEmbeddedPdfHeader ? content.slice(pdfHeaderIndex) : content;
      const eofIndex = fromHeader.lastIndexOf('%%EOF');
      binaryPayload = eofIndex >= 0 ? fromHeader.slice(0, eofIndex + 5) : fromHeader;
    } else {
      try {
        binaryPayload = atob(base64Payload);
      } catch (err) {
        console.warn('Failed to decode base64 PDF content', err);
        binaryPayload = null;
      }
    }

    if (!binaryPayload) {
      return undefined;
    }

    const buffer = new Uint8Array(binaryPayload.length);
    for (let i = 0; i < binaryPayload.length; i += 1) {
      buffer[i] = binaryPayload.charCodeAt(i) & 0xff;
    }

    return buffer;
  }, [content, fileUrl]);

  const [objectUrl, setObjectUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!fileData) {
      setObjectUrl(undefined);
      return undefined;
    }

    const blob = new Blob([fileData], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [fileData]);

  const resolvedUrl = fileUrl ?? objectUrl;

  const contentUrl = useMemo(() => {
    if (!content) return undefined;
    const trimmed = content.trim();

    const directUrl = trimmed.match(/^<?(https?:\/\/[^\s>]+)>?$/i)?.[1];
    if (directUrl) {
      return directUrl;
    }

    const markdownUrl = trimmed.match(/\[[^\]]+\]\((https?:\/\/[^)]+)\)/i)?.[1];
    if (markdownUrl) {
      return markdownUrl;
    }

    return undefined;
  }, [content]);

  const resolvedFileUrl = resolvedUrl ?? contentUrl;

  if (!resolvedFileUrl) {
    return (
      <div className={clsx(styles.viewerRoot, className)} style={{ height }}>
        <div className={styles.message}>PDF content unavailable.</div>
        {sourceUrl && (
          <div className={styles.downloadLink}>
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
              Open original document
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={clsx(styles.viewerRoot, className)} style={{ height }}>
      <iframe
        title="PDF Preview"
        src={resolvedFileUrl}
        className={styles.nativeFrame}
      />
      <div className={styles.downloadLink}>
        <a
          href={sourceUrl || (typeof resolvedFileUrl === 'string' ? resolvedFileUrl : undefined)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open original document
        </a>
      </div>
    </div>
  );
};

export default PdfViewer;
